import React, { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Check, Plus, Users, X } from 'lucide-react'

import { getCurrentSession, supabase } from '../lib/supabase'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

interface ManagersConfigurationProps {
  teamId: string
  userRole: 'owner' | 'admin' | 'member' | 'manager'
}

interface Pod {
  id: string
  name: string
}

interface TeamMember {
  id: string
  auth_user_id: string | null
  role: 'owner' | 'admin' | 'member' | 'manager'
  user_name: string | null
  user_email: string | null
  avatar_url: string | null
}

interface ManagerAssignment {
  pod_id: string
  auth_user_id: string
}

const POD_COLORS = ['bg-amber-500', 'bg-emerald-600', 'bg-cyan-600', 'bg-pink-500', 'bg-violet-600', 'bg-blue-500']

const displayName = (member: TeamMember) => member.user_name || member.user_email?.split('@')[0] || 'Unknown user'

const initials = (name: string) => name.split(' ').map((part) => part[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?'

const colorForPod = (index: number) => POD_COLORS[index % POD_COLORS.length]

export const ManagersConfiguration: React.FC<ManagersConfigurationProps> = ({ teamId, userRole }) => {
  const [pods, setPods] = useState<Pod[]>([])
  const [members, setMembers] = useState<TeamMember[]>([])
  const [assignments, setAssignments] = useState<ManagerAssignment[]>([])
  const [addingToPod, setAddingToPod] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  // ROLE AUDIT (2026-10-09): admin-only, not admin+manager -- but only for
  // assigning/unassigning the manager role itself (the write actions below).
  // The list of current managers per pod is shown to everyone regardless of
  // isAdmin; only granting/revoking manager-ship is gated, deliberately, so
  // a manager can't promote or demote peers.
  const isAdmin = userRole === 'admin' || userRole === 'owner'

  const loadConfiguration = async () => {
    setLoading(true)
    setError('')

    try {
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')

      const [podsResult, membersResult] = await Promise.all([
        (supabase.from as any)('pods').select('id, name').eq('team_id', teamId),
        supabase.functions.invoke('get-all-team-users', {
          body: { team_id: teamId },
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
      ])

      if (podsResult.error) throw podsResult.error
      if (membersResult.error) throw membersResult.error

      const loadedPods = (podsResult.data ?? []) as Pod[]
      setPods(loadedPods)
      setMembers((membersResult.data?.users ?? []) as TeamMember[])

      if (loadedPods.length === 0) {
        setAssignments([])
        return
      }

      const managerResult = await (supabase.from as any)('pod_managers')
        .select('pod_id, auth_user_id')
        .in('pod_id', loadedPods.map((pod) => pod.id))
      if (managerResult.error) throw managerResult.error
      setAssignments((managerResult.data ?? []) as ManagerAssignment[])
    } catch (loadError: any) {
      setError(loadError.message || 'Failed to load team managers')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadConfiguration()
  }, [teamId])

  const membersByAuthId = useMemo(
    () => new Map(members.filter((member) => member.auth_user_id).map((member) => [member.auth_user_id as string, member])),
    [members],
  )

  const managersForPod = (podId: string) => assignments.filter((assignment) => assignment.pod_id === podId)

  const managerCandidatesForPod = () => {
    return members.filter((member) => member.auth_user_id && member.role !== 'admin' && member.role !== 'owner')
  }

  const updateManager = async (podId: string, authUserId: string, action: 'assign_manager' | 'unassign_manager', keepPickerOpen = false) => {
    if (!isAdmin) return
    const key = `${podId}:${authUserId}`
    setSavingKey(key)
    setError('')
    setSuccess('')

    try {
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')
      const { data, error: invokeError } = await supabase.functions.invoke('manage-pods', {
        body: { action, team_id: teamId, pod_id: podId, manager_auth_user_id: authUserId },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (invokeError) throw invokeError
      if (data?.error) throw new Error(data.error)

      setAssignments((current) => action === 'assign_manager'
        ? [...current, { pod_id: podId, auth_user_id: authUserId }]
        : current.filter((assignment) => !(assignment.pod_id === podId && assignment.auth_user_id === authUserId)))
      if (!keepPickerOpen) setAddingToPod(null)
      setSuccess(action === 'assign_manager' ? 'Manager added.' : 'Manager removed.')
    } catch (updateError: any) {
      setError(updateError.message || 'Failed to update manager assignment')
    } finally {
      setSavingKey(null)
    }
  }

  if (loading) {
    return (
      <div className="bg-background">
        <div className="space-y-3 border-b border-border p-4 sm:p-6"><Skeleton className="h-6 w-40" /><Skeleton className="h-4 w-96 max-w-full" /></div>
        {[0, 1, 2, 3].map((row) => <div key={row} className="flex items-center justify-between border-b border-border p-4 sm:p-6"><Skeleton className="h-5 w-28" /><Skeleton className="h-9 w-64" /></div>)}
        <div className="flex justify-end p-4 sm:p-6"><Skeleton className="h-10 w-32" /></div>
      </div>
    )
  }

  return (
    <div className="bg-background">
      <div className="border-b border-border p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-foreground">Team managers</h2>
        <p className="mt-1 text-sm text-muted-foreground">Who&apos;s accountable for each team&apos;s standups and gets escalation alerts.</p>
      </div>

      {error && <div role="alert" className="flex items-center gap-2 border-b border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive sm:px-6"><AlertCircle className="h-4 w-4 shrink-0" />{error}</div>}
      {success && <div role="status" className="flex items-center gap-2 border-b border-primary/30 bg-primary/10 px-4 py-3 text-sm text-foreground sm:px-6"><Check className="h-4 w-4 shrink-0" />{success}</div>}

      <div className="divide-y divide-border">
        {pods.map((pod, index) => (
          <div key={pod.id} className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div className="flex items-center gap-3">
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${colorForPod(index)}`}>{pod.name.slice(0, 1).toUpperCase()}</div>
              <span className="text-sm font-medium text-foreground">{pod.name}</span>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 sm:max-w-[72%]">
              {managersForPod(pod.id).map((assignment) => {
                const member = membersByAuthId.get(assignment.auth_user_id)
                if (!member) return null
                const key = `${pod.id}:${assignment.auth_user_id}`
                return (
                  <div key={assignment.auth_user_id} className="flex h-10 items-center gap-2 rounded-md border border-border bg-background px-2.5 text-sm text-foreground">
                    <Avatar className="h-7 w-7"><AvatarImage src={member.avatar_url ?? undefined} alt="" /><AvatarFallback className="text-[10px] font-semibold">{initials(displayName(member))}</AvatarFallback></Avatar>
                    <span>{displayName(member)}</span>
                    {isAdmin && <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground" disabled={savingKey === key} onClick={() => void updateManager(pod.id, assignment.auth_user_id, 'unassign_manager')} aria-label={`Remove ${displayName(member)} as manager`}><X className="h-4 w-4" /></Button>}
                  </div>
                )
              })}

              {isAdmin && (
                <Popover open={addingToPod === pod.id} onOpenChange={(open) => setAddingToPod(open ? pod.id : null)}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className="h-10 border-dashed border-primary text-primary hover:bg-primary/10"><Plus className="h-4 w-4" />Add Manager</Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-64 p-2">
                    <div className="flex flex-col gap-1">
                      <p className="px-2 py-1 text-xs font-medium text-muted-foreground">Select managers</p>
                      {managerCandidatesForPod().map((member) => {
                        const authUserId = member.auth_user_id as string
                        const checked = managersForPod(pod.id).some((assignment) => assignment.auth_user_id === authUserId)
                        const key = `${pod.id}:${authUserId}`
                        return (
                          <label key={authUserId} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-muted">
                            <Checkbox
                              checked={checked}
                              disabled={savingKey === key}
                              onCheckedChange={(nextChecked) => void updateManager(pod.id, authUserId, nextChecked ? 'assign_manager' : 'unassign_manager', true)}
                              aria-label={`${checked ? 'Remove' : 'Add'} ${displayName(member)} ${checked ? 'as' : 'from'} manager`}
                            />
                            <Avatar className="size-7"><AvatarImage src={member.avatar_url ?? undefined} alt="" /><AvatarFallback className="text-[10px] font-semibold">{initials(displayName(member))}</AvatarFallback></Avatar>
                            <span className="truncate">{displayName(member)}</span>
                          </label>
                        )
                      })}
                      {managerCandidatesForPod().length === 0 && <p className="px-2 py-2 text-sm text-muted-foreground">No eligible members.</p>}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
        ))}
        {pods.length === 0 && <div className="flex flex-col items-center gap-2 p-12 text-center"><Users className="h-8 w-8 text-muted-foreground" /><p className="text-sm font-medium text-foreground">No teams yet</p><p className="text-sm text-muted-foreground">Create a team before assigning managers.</p></div>}
      </div>

      <div className="flex flex-col gap-3 border-t border-border p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        {!isAdmin && <p className="text-sm text-muted-foreground">Only team admins can change manager assignments.</p>}
        {/* Was a "Save Changes" button that didn't save anything -- every
            assign/unassign already writes immediately via updateManager()
            above (manage-pods edge function), so there was never anything
            left to save. Replaced with a passive note instead of a button
            that implied unsaved changes could exist. */}
        <p className="ml-auto flex items-center gap-1.5 text-sm text-muted-foreground">
          <Check className="h-4 w-4" />Changes save automatically
        </p>
      </div>
    </div>
  )
}
